/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { browser } from '@atlaskit/editor-common/browser';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import LinkIcon from '@atlaskit/icon/core/link';
import PersonIcon from '@atlaskit/icon/core/person';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { PromptEditorWrapper } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import type { PromptEditor } from '../../components/PromptEditorWrapper/PromptEditorWrapper';

import { useEscapeKey, useSubmitControls } from './hooks';
import messages from './messages';

type Props = {
	textInput?: string;
	adfInput?: JSONDocNode;
	onTextChange: (value: string) => void;
	onADFChange?: (value: JSONDocNode) => void;
	submit: () => void;
	cancel: () => void;
	placeholder?: string;
	PromptEditor: PromptEditor;
};

const container = cssMap({
	padding: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: {
			borderRadius: token('border.radius.200'),
		},
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
});

const inputField = cssMap({
	styles: {
		marginBlockStart: token('space.050'),
		wordBreak: 'break-word',
		flexGrow: 1,
		flexShrink: 1,
		width: 'auto',
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
	},
});

const insertMenu = cssMap({
	menuWidth: {
		minWidth: '260px',
	},
	marginFix: {
		marginLeft: 'auto',
		marginTop: 'auto',
	},
});

const insertMenuItem = cssMap({
	grow: {
		flex: 1,
	},
});

interface InsertMenuItemDef {
	Icon: typeof LinkIcon;
	message: keyof typeof messages;
	shortcut: string;
}

const InsertMenuButton = memo(() => {
	const intl = useIntl();
	const metaKey = browser.mac ? 'Cmd' : 'Ctrl';
	const INSERT_ITEMS: readonly InsertMenuItemDef[] = [
		{ Icon: LinkIcon, message: 'insertMenuAddLink', shortcut: `${metaKey}+k` },
		{ Icon: PersonIcon, message: 'insertMenuMention', shortcut: '@' },
	];

	return (
		<DropdownMenu<HTMLButtonElement>
			placement="bottom-end"
			shouldRenderToParent
			trigger={({ triggerRef, ...trigProps }) => (
				<IconButton
					isSelected={trigProps.isSelected}
					onClick={trigProps.onClick}
					onFocus={trigProps.onFocus}
					onBlur={trigProps.onBlur}
					testId={trigProps.testId}
					aria-controls={trigProps['aria-controls']}
					aria-expanded={trigProps['aria-expanded']}
					aria-haspopup={trigProps['aria-haspopup']}
					data-ds--level={trigProps['data-ds--level']}
					ref={triggerRef}
					appearance="subtle"
					icon={AddIcon}
					label={intl.formatMessage(messages.insertMenuTriggerLabel)}
				/>
			)}
		>
			<Box xcss={insertMenu.menuWidth}>
				<DropdownItemGroup>
					{INSERT_ITEMS.map(({ Icon, message, shortcut }) => (
						<DropdownItem key={message}>
							<Inline alignBlock="center" space="space.200">
								<Icon label="" />
								<Box xcss={insertMenuItem.grow}>{intl.formatMessage(messages[message])}</Box>
								<Tag
									color="standard"
									appearance="rounded"
									text={shortcut}
									aria-label={`keyboard shortcut ${shortcut}`}
								/>
							</Inline>
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</Box>
		</DropdownMenu>
	);
});

export const PromptScreen = ({
	textInput,
	adfInput,
	onTextChange,
	onADFChange,
	submit,
	cancel,
	PromptEditor,
}: Props) => {
	const intl = useIntl();
	const screenRef = useRef<HTMLDivElement | null>(null);
	useEscapeKey(screenRef, cancel);
	const { isDisabled: isSubmitDisabled, onKeyDown } = useSubmitControls(submit, textInput);
	const placeholder = `${intl.formatMessage(messages.promptPlaceholder)}...`;

	return (
		<FloatingContainer
			ref={screenRef}
			data-testid="prompt-form-screen"
			disablePadding
			rainbowBorder={false}
			enableMaxHeight
		>
			<Inline xcss={container.padding}>
				<Box xcss={inputField.styles}>
					<PromptEditorWrapper
						autoFocus
						PromptEditor={PromptEditor}
						type="user-input"
						defaultValue={adfInput}
						onInputChange={onTextChange}
						onADFChange={onADFChange}
						placeholder={placeholder}
						enableLinks
						onKeyDown={onKeyDown}
					/>
				</Box>
				<Inline xcss={insertMenu.marginFix} space="space.050">
					<InsertMenuButton />
					<div>
						<IconButton
							appearance="primary"
							icon={ArrowUpIcon}
							label={intl.formatMessage(messages.promptSubmit)}
							isDisabled={isSubmitDisabled}
							onClick={submit}
						/>
					</div>
				</Inline>
			</Inline>
		</FloatingContainer>
	);
};
